import type { Session } from './api';

type Reading = Session['meter_values'][number];

const FRESH_MS = 5 * 60_000;
const CLOCK_SKEW_MS = 60_000;
// Largest charger rating accepted by the network submission flow.
const MAX_ESTIMATED_KW = 1000;

interface MeterPower {
  reading: Reading;
  at: number;
  kw: number | null;
  estimated: boolean;
}

function interpret(reading: Reading, at: number, previous?: { reading: Reading; at: number }): MeterPower {
  const row: MeterPower = { reading, at, kw: null, estimated: false };
  const power = reading.power_kw;
  if (Number.isFinite(power) && power > 0) return { ...row, kw: power };
  // Older normalized samples store zero for an omitted power measurand.
  // A simultaneous near-zero current is independent evidence of a stopped load.
  if (power === 0 && typeof reading.current === 'number' && reading.current >= 0 && reading.current < 0.1) {
    return { ...row, kw: 0 };
  }
  if (!previous) return row;
  const elapsed = at - previous.at;
  if (elapsed < 1000 || elapsed > FRESH_MS) return row;
  const before = previous.reading.energy_kwh;
  const after = reading.energy_kwh;
  if (!Number.isFinite(before) || !Number.isFinite(after) || before < 0 || after < before) return row;
  if (after === before && (reading.current ?? 0) >= 0.1) return row;
  const kw = (after - before) * 3_600_000 / elapsed;
  return kw <= MAX_ESTIMATED_KW ? { ...row, kw, estimated: true } : row;
}

/** Use only readings from this session, in meter time, including the sample before the first visible row. */
export function recentMeterPower(readings: readonly Reading[] | undefined, limit: number): MeterPower[] {
  const samples = (readings ?? []).map((reading) => ({ reading, at: Date.parse(reading.timestamp) }))
    .filter(({ at }) => Number.isFinite(at))
    .sort((a, b) => b.at - a.at || Number(b.reading.power_kw > 0) - Number(a.reading.power_kw > 0));
  let previous = 0;
  return samples.slice(0, limit).map(({ reading, at }) => {
    // Preserve both rows at the same time, but use the next distinct meter time for the interval.
    while (samples[previous] && samples[previous]!.at >= at) previous++;
    return interpret(reading, at, samples[previous]);
  });
}

export function meterPowerText(row: MeterPower | undefined, decimals: number): string {
  if (row?.kw == null) return 'Not available';
  return row.kw.toFixed(decimals) + ' kW' + (row.estimated ? ' (est.)' : '');
}

export function livePowerText(row: MeterPower | undefined, now = Date.now()): string {
  if (!row) return 'Awaiting reading';
  if (now - row.at > FRESH_MS || row.at - now > CLOCK_SKEW_MS) return 'No recent reading';
  return meterPowerText(row, 1);
}
